/**
 * CroppedImg — drop-in replacement for Remotion's <Img> that applies
 * a crop region sent from the Timeline app.
 *
 * Crop format (matches Timeline's clip.transforms.crop):
 *   { x, y, width, height, sourceWidth, sourceHeight }
 *   All values in source-pixel coordinates.
 *
 * If no crop is provided, renders a normal cover-fit <Img>.
 *
 * Technique: renders the image oversized inside an overflow:hidden
 * container, positioned so only the crop region is visible.
 */

import React, { useState, useCallback } from "react";
import { Img } from "remotion";

/**
 * @param {string}  src             – image URL
 * @param {object}  [crop]          – { x, y, width, height, sourceWidth, sourceHeight }
 * @param {object}  [style]         – extra styles on the <Img>
 * @param {object}  [containerStyle] – extra styles on the wrapper div
 */
export default function CroppedImg({ src, crop, style = {}, containerStyle = {} }) {
  if (!src) return null;

  // If crop has x/y/width/height but is missing sourceWidth/sourceHeight,
  // we'll measure via onLoad and re-render.
  const [measuredDims, setMeasuredDims] = useState(null);

  const onLoad = useCallback((e) => {
    if (e?.target) {
      const nw = e.target.naturalWidth;
      const nh = e.target.naturalHeight;
      if (nw && nh) setMeasuredDims({ w: nw, h: nh });
    }
  }, []);

  // Resolve sourceWidth/sourceHeight: crop-embedded → measured → 0
  const sourceWidth = crop?.sourceWidth || measuredDims?.w || 0;
  const sourceHeight = crop?.sourceHeight || measuredDims?.h || 0;

  const hasCrop =
    crop &&
    crop.width > 0 &&
    crop.height > 0 &&
    sourceWidth > 0 &&
    sourceHeight > 0 &&
    // Not a full-frame "crop" (no-op)
    !(crop.x === 0 && crop.y === 0 &&
      crop.width === sourceWidth && crop.height === sourceHeight);

  // Need to measure? Render a hidden Img to get natural dims
  const needsMeasure =
    crop && crop.width > 0 && crop.height > 0 &&
    !crop.sourceWidth && !crop.sourceHeight && !measuredDims;

  if (!hasCrop) {
    return (
      <div style={{ width: "100%", height: "100%", overflow: "hidden", ...containerStyle }}>
        <Img
          src={src}
          onLoad={needsMeasure ? onLoad : undefined}
          style={{ width: "100%", height: "100%", objectFit: "cover", ...style }}
        />
      </div>
    );
  }

  // The container is 100% × 100% (whatever the parent dictates).
  // We want to show *only* the crop region, filling the container.
  //
  // Scale the image so that crop.width maps to 100% of container width:
  //   imgWidth  = (sourceWidth / crop.width)  * 100%
  //   imgHeight = (sourceHeight / crop.height) * 100%
  //
  // Offset so crop origin (x, y) maps to container origin (0, 0):
  //   left = -(crop.x / crop.width) * 100%
  //   top  = -(crop.y / crop.height) * 100%

  const imgW = (sourceWidth / crop.width) * 100;
  const imgH = (sourceHeight / crop.height) * 100;
  const offX = -(crop.x / crop.width) * 100;
  const offY = -(crop.y / crop.height) * 100;

  // Strip geometry props from caller style so they can't override crop math
  const { width: _w, height: _h, left: _l, top: _t, position: _p, objectFit: _o, ...safeStyle } = style;

  return (
    <div
      style={{
        width: "100%",
        height: "100%",
        overflow: "hidden",
        position: "relative",
        ...containerStyle,
      }}
    >
      <Img
        src={src}
        style={{
          position: "absolute",
          width: `${imgW}%`,
          height: `${imgH}%`,
          left: `${offX}%`,
          top: `${offY}%`,
          objectFit: "cover",
          ...safeStyle,
        }}
      />
    </div>
  );
}

;
    var _remotion_globalVariableA, _remotion_globalVariableB;
    // Legacy CSS implementations will `eval` browser code in a Node.js context
    // to extract CSS. For backwards compatibility, we need to check we're in a
    // browser context before continuing.
    if (typeof self !== 'undefined' &&
        // AMP / No-JS mode does not inject these helpers:
        '$RefreshHelpers$' in self) {
        const currentExports = __webpack_module__.exports;
        const prevExports = (_remotion_globalVariableB = (_remotion_globalVariableA = __webpack_module__.hot.data) === null || _remotion_globalVariableA === void 0 ? void 0 : _remotion_globalVariableA.prevExports) !== null && _remotion_globalVariableB !== void 0 ? _remotion_globalVariableB : null;
        // This cannot happen in MainTemplate because the exports mismatch between
        // templating and execution.
        self.$RefreshHelpers$.registerExportsForReactRefresh(currentExports, __webpack_module__.id);
        // A module can be accepted automatically based on its exports, e.g. when
        // it is a Refresh Boundary.
        if (self.$RefreshHelpers$.isReactRefreshBoundary(currentExports)) {
            // Save the previous exports on update so we can compare the boundary
            // signatures.
            __webpack_module__.hot.dispose((data) => {
                data.prevExports = currentExports;
            });
            // Unconditionally accept an update to this module, we'll check if it's
            // still a Refresh Boundary later.
            __webpack_module__.hot.accept();
            // This field is set when the previous version of this module was a
            // Refresh Boundary, letting us know we need to check for invalidation or
            // enqueue an update.
            if (prevExports !== null) {
                // A boundary can become ineligible if its exports are incompatible
                // with the previous exports.
                //
                // For example, if you add/remove/change exports, we'll want to
                // re-execute the importing modules, and force those components to
                // re-render. Similarly, if you convert a class component to a
                // function, we want to invalidate the boundary.
                if (self.$RefreshHelpers$.shouldInvalidateReactRefreshBoundary(prevExports, currentExports)) {
                    __webpack_module__.hot.invalidate();
                }
                else {
                    self.$RefreshHelpers$.scheduleUpdate();
                }
            }
        }
        else {
            // Since we just executed the code for the module, it's possible that the
            // new exports made it ineligible for being a boundary.
            // We only care about the case when we were _previously_ a boundary,
            // because we already accepted this update (accidental side effect).
            const isNoLongerABoundary = prevExports !== null;
            if (isNoLongerABoundary) {
                __webpack_module__.hot.invalidate();
            }
        }
    }
